import * as Telecom from '@servicetitan/telecom-rtcs-client';
import { userStatsFromAgentStats } from '../../types/ServiceUser';
import appManager from '@app/appManager';
import {
  ServiceSessionEventType,
  ServiceSessionUserEvent,
} from '../../types/ServiceEvents';
import requireUser from '../../lib/requireUser';
import log from '../../lib/log';
import { setUserStatus } from '../../lib/userStatus';
import isValidUser from '../../lib/isValidUser';
import { ServiceUserUtils } from '../../lib/serviceUserUtils';
import {
  UserActivityStatus,
  UserSetAvailabilityStatus,
} from '@api/generated/graphql';
import { EventAvailabilityState } from '../../lib/eventAvailabilityState';
import { RTCSEventType } from '../../../metrics/rtcsMetrics';
import { trackRTCSEventLatency } from '../../lib/trackRTCSEventLatency';

/**
 * The hangup event indicates that a session has been hung-up
 * after being handled by the recipient.
 * @param event
 */
async function handleAgentSessionTerminated(
  event: Telecom.AgentSessionTerminatedEvent,
) {
  // Track RTCS event latency - CRITICAL for diagnosing ghost ringing after hangup
  trackRTCSEventLatency(event.timestamp, RTCSEventType.SESSION_TERMINATED);

  if (!isValidUser(event.agentId)) {
    return;
  }

  const user = await requireUser(event.agentId);
  log('rtcs:agent:session_terminated:' + user?.name, event);

  user.available = event.available;
  user.capacity = 1;
  user.stats = userStatsFromAgentStats(event.stats);

  // update our status. status can be changed again
  setUserStatus({
    userId: user.id,
    status: event.available ? UserSetAvailabilityStatus.Available : undefined,
    canChange: true,
  });

  // This fix race condition with event: rtcs:agent:wrapup_started
  // if the user is in wrapup time, we should not change the activity status to None
  if (ServiceUserUtils.isUserPhase2()) {
    if (
      ServiceUserUtils.getActivityStatus(user) !== UserActivityStatus.WrapUp
    ) {
      ServiceUserUtils.setEventActivityStatus(user, '');
    }
  } else {
    // else block for non-phase2 users
    if (user.availabilityState !== EventAvailabilityState.WrapupTime) {
      ServiceUserUtils.setEventActivityStatus(user, '');
    }
  }

  appManager.events.emit<ServiceSessionUserEvent>(
    ServiceSessionEventType.HANGUP,
    {
      sessionId: event.conversationId,
      sessionSid: event.conversationSid,
      agentId: event.agentId,
      userId: user.id,
    },
  );
}

export default handleAgentSessionTerminated;
